import { useCallback, useEffect, useRef, useState } from 'react';

import { SearchOutlined } from '@mui/icons-material';
import { Button, IconButton, InputAdornment, Stack } from '@mui/material';

import { MarcaAutoComplete } from 'components/autocompletes/MarcaAutoComplete';
import { CissTextField } from 'components/fields';
import { ContentModal } from 'components/modal';
import { SubTitle } from 'components/text';
import { ListagemProvider, useListagemContext } from 'context/ListagemContext';
import { ConfigPedidoProdutosModalGrid } from 'pages/compra/configuracao-pedido/components';
import { ProdutosProps } from 'pages/compra/configuracao-pedido/types';

type ConfigPedidoProdutosModal = {
    open: boolean;
    setOpen: (action: boolean) => void;
    handleAdicionarProdutos: (products: ProdutosProps[]) => void;
};

function ConfigPedidoProdutosModalContent({ open, setOpen, handleAdicionarProdutos }: ConfigPedidoProdutosModal) {
    const { setPage } = useListagemContext();
    const [valueFilter, setValueFilter] = useState('');
    const [selectedMarca, setSelectedMarca] = useState<{ idMarca: number } | null>(null);
    const [selectedProducts, setSelectedProducts] = useState<ProdutosProps[]>([]);
    const produtoMapRef = useRef<Map<number, ProdutosProps>>(new Map());
    const inputRef = useRef<HTMLInputElement>(null);

    /**
     * Atualiza o valor do filtro de pesquisa.
     */
    const handleSetValueFilter = (value: string) => {
        setValueFilter(value);

        setPage(1);
    };

    /**
     * Reseta os valores do modal.
     */
    const resetValues = useCallback(() => {
        if (!open) {
            setSelectedProducts([]);
            setSelectedMarca(null);
            setValueFilter('');
            setPage(1);
            produtoMapRef.current.clear();
        }
    }, [open, setPage]);

    useEffect(resetValues, [resetValues]);

    return (
        <ContentModal
            closeButton
            open={open}
            setOpen={setOpen}
            sx={{
                padding: 2,
                borderRadius: 1,
                width: {
                    xs: '90vw',
                    md: 800,
                },
            }}
        >
            <>
                <SubTitle label="Incluir Produtos" />

                <Stack direction="row" sx={{ gap: 1, mb: 2, alignItems: 'center' }}>
                    <MarcaAutoComplete
                        size="small"
                        label="Marca"
                        sx={{ flex: 1 }}
                        value={selectedMarca}
                        onChange={(_e, value) => {
                            setSelectedMarca(value);
                            setPage(1);
                        }}
                    />

                    <CissTextField
                        inputRef={inputRef}
                        placeholder="Pesquisar por código ou nome"
                        label="Pesquisar"
                        size="small"
                        sx={{ flex: 1 }}
                        deboundTime={500}
                        value={valueFilter}
                        onChange={(e) => handleSetValueFilter(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();

                                if (inputRef.current) {
                                    handleSetValueFilter(inputRef.current.value);
                                }
                            }
                        }}
                        slotProps={{
                            input: {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            edge="end"
                                            onClick={() => {
                                                if (inputRef.current) {
                                                    handleSetValueFilter(inputRef.current.value);
                                                }
                                            }}
                                        >
                                            <SearchOutlined />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                </Stack>

                <ConfigPedidoProdutosModalGrid
                    open={open}
                    valueFilter={valueFilter}
                    produtoMapRef={produtoMapRef}
                    selectedMarca={selectedMarca}
                    selectedProducts={selectedProducts}
                    setSelectedProducts={setSelectedProducts}
                />

                <Stack direction="row" spacing={2} sx={{ justifyContent: 'flex-end', mt: 2 }}>
                    <Button variant="outlined" onClick={() => setOpen(false)}>
                        Cancelar
                    </Button>

                    <Button
                        variant="contained"
                        disabled={!selectedProducts.length}
                        onClick={() => {
                            handleAdicionarProdutos(selectedProducts);
                            setOpen(false);
                        }}
                    >
                        Incluir
                    </Button>
                </Stack>
            </>
        </ContentModal>
    );
}

export function ConfigPedidoProdutosModal(props: ConfigPedidoProdutosModal) {
    return (
        <ListagemProvider>
            <ConfigPedidoProdutosModalContent {...props} />
        </ListagemProvider>
    );
}
